import { type FC, useEffect } from "react";
import { toast } from "react-toastify";
import { useCreateMediaCompanyShortMutation } from "../../api/clientAPI";
import Form from "../../components/Form/Form";
import { ValidatorsE } from "../../components/Form/yupValidations";
import { useAppDispatch } from "../../store/hooks";
import { setSelectedMediaCompany } from "../../store/slices/deliveryWizardSlice";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import usePopup from "../../utils/hooks/usePopup";
import { useTranslate } from "../../utils/hooks/useTranslate";

import type { FormFieldT } from "../../models/models";

type CreateNewMediaCompanyShortBodyPropTypes = {
  mediaCompanyName?: string;
};

const CreateNewMediaCompanyShortBody: FC<
  CreateNewMediaCompanyShortBodyPropTypes
> = ({ mediaCompanyName }) => {
  const dispatch = useAppDispatch();
  const [createMediaCompany] = useCreateMediaCompanyShortMutation();
  const { t } = useTranslate();

  // One name field, either language: the backend derives the AD-ID from it (transliterating Hebrew)
  // and falls back to MC#### when that yields nothing usable, so there is no second name to collect
  // and a save can no longer fail on the AD-ID. The three contact fields are optional and are
  // stored as the new client's ClientInfo row.
  // No addAction is passed, so each hintText renders the (i) icon rather than a blue "+".
  //
  // Every field is length-capped here, at the only layer that can show the user a usable message:
  // ClientInfoManager.CheckItem throws a raw ArgumentException past 200/200/50, and the controller
  // swallows that failure, so an uncapped over-long value would drop the contact details silently.
  const fields: FormFieldT[] = [
    {
      label: "Company Name",
      name: "name",
      validation: ValidatorsE.REQUIRED_MAX_100_WITH_NO_COMMA_AND_SEMICOLON,
      defaultValue: mediaCompanyName,
      hintText: "The name of the media company, in Hebrew or in English.",
    },
    {
      // Otherwise unvalidated, matching CreateEditClient and the ClientInfo popup.
      label: "Contact Name",
      name: "contactName",
      validation: ValidatorsE.MAX_100,
      hintText:
        "The name of the contact person at the media company. Optional.",
    },
    {
      label: "Email",
      name: "contactEmail",
      validation: ValidatorsE.EMAIL_NOT_REQUIRED_MAX_100,
      hintText:
        "The email address of the contact person at the media company. Optional.",
    },
    {
      label: "Phone number",
      name: "contactPhone",
      validation: ValidatorsE.PHONE_NUMBER_NOT_REQUIRED_MAX_10_DIGITS,
      hintText:
        "The phone number of the contact person at the media company. Optional.",
    },
  ];
  const { removePopup } = usePopup();

  useEffect(() => {
    const nameField = document.getElementsByName("name")?.[0];

    if (nameField) {
      nameField.focus();
    }
  }, []);

  const handleSubmit = async (data: any) => {
    // A blank contact field is sent as an empty string; the backend treats null and
    // whitespace-only identically to blank and simply writes no ClientInfo row.
    const body = {
      Name: (data.name ?? "").trim(),
      ContactName: (data.contactName ?? "").trim(),
      Email: (data.contactEmail ?? "").trim(),
      Phone: (data.contactPhone ?? "").trim(),
    };

    try {
      const res: any = await createMediaCompany(body);

      if (res.error) {
        // Backend returns HTTP 409 + localized text (e.g. duplicate) as error.data.
        const message =
          typeof res.error?.data === "string"
            ? res.error.data
            : t`Failed to create media company`;
        toast.error(message, { autoClose: TOAST_DURATION });
        return;
      }

      dispatch(
        setSelectedMediaCompany({ value: res.data.Id, label: res.data.Name })
      );
      removePopup();
      toast("Successfully created", {
        type: "success",
        autoClose: TOAST_DURATION,
      });
    } catch (e: any) {
      console.log(e);
    }
  };

  return (
    <Form
      fields={fields}
      formID="create-media-company-short"
      onSubmit={handleSubmit}
      columns={1}
    />
  );
};

export default CreateNewMediaCompanyShortBody;
